import { ReportDraftKernel } from "./ReportDraftKernel";
import { dataUrlToBlob, isQuotaExceeded } from "./dataUrlToBlob";

// Mixin partilhado de rascunho offline (BEL-1578 M2). O anfitrião deve ter
// this.form (com visit_id) e declarar this.reportType ('linear'|'attendance'|
// 'competitorshopping'). Exposto em window.reportDraftMixin para os mixins
// inline no Blade (Attendance/Competitor) o usarem sem `import`.
export const reportDraftMixin = {
	data() {
		return {
			reportDraftKernel: null,
			hasProgress: false,
			// Segundos restantes até "Apagar rascunho" voltar a ficar disponível
			// após uma gravação. 0 = disponível. Evita o mis-click no mesmo sítio
			// onde se grava/atualiza (BEL-1580). Puramente cliente, logo funciona offline.
			deleteCountdown: 0,
		};
	},
	created() {
		this.reportDraftKernel = new ReportDraftKernel({
			csrfToken: document.querySelector('meta[name="csrf-token"]')?.content || null,
		});
		this._draftObjectUrls = {};
	},
	mounted() {
		// Delegação: remover uma foto de rascunho re-hidratada (cartão placeholder).
		this._onDraftPhotoDeleteClick = event => {
			const btn = event.target.closest(".deleteDraftPhoto");
			if (btn) {
				event.preventDefault();
				this.removeDraftPhoto(btn.getAttribute("data-uuid"));
			}
		};
		document.addEventListener("click", this._onDraftPhotoDeleteClick);
	},
	beforeDestroy() {
		if (this._deleteCountdownTimer) {
			clearInterval(this._deleteCountdownTimer);
			this._deleteCountdownTimer = null;
		}
		if (this._onDraftPhotoDeleteClick) {
			document.removeEventListener("click", this._onDraftPhotoDeleteClick);
		}
		if (this._draftObjectUrls) {
			Object.values(this._draftObjectUrls).forEach(url => URL.revokeObjectURL(url));
			this._draftObjectUrls = {};
		}
	},
	computed: {
		draftKeyStorage() {
			return (this.form && this.form.visit_id ? this.form.visit_id : "") + "progress_" + this.reportType;
		},
	},
	methods: {
		persistDraft(payload, meta = {}) {
			localStorage.setItem(this.draftKeyStorage, JSON.stringify(payload));
			if (this.reportDraftKernel && this.form.visit_id && this.reportType) {
				this.reportDraftKernel.saveDraft(this.form.visit_id, this.reportType, this.withDirty(payload), meta);
			}
			this.hasProgress = true;
		},
		seedDraftFromLocal() {
			if (this.reportDraftKernel && this.form.visit_id && this.reportType) {
				this.reportDraftKernel.saveDraft(this.form.visit_id, this.reportType, this.withDirty(this.form), {});
			}
		},
		// Snapshot the form answers (per product → field values) at load time so
		// every later server write can diff against it (BEL-1578 §2.1b). The
		// baseline is the draft as the operator first saw it — including any
		// Conferência edits already merged in.
		captureDraftBaseline() {
			this._draftBaseline = this.snapshotAnswers(this.form);
		},
		snapshotAnswers(form) {
			const snap = {};
			if (!form || !Array.isArray(form.answers)) {
				return snap;
			}
			for (const row of form.answers) {
				const inner = row && row.answers ? row.answers : row;
				const pid = inner && inner.product ? inner.product.id : row && row.product ? row.product.id : null;
				if (pid == null) {
					continue;
				}
				snap[String(pid)] = JSON.parse(JSON.stringify(inner));
			}
			return snap;
		},
		// Per-product map of the fields the operator actually changed since load.
		// Returns null when no baseline exists, signalling the server to fall
		// back to a whole-payload replace (legacy behaviour, no merge).
		computeDirtyFields(form) {
			const src = form || this.form;
			const baseline = this._draftBaseline;
			if (!baseline || !src || !Array.isArray(src.answers)) {
				return null;
			}
			const dirty = {};
			for (const row of src.answers) {
				const inner = row && row.answers ? row.answers : row;
				const pid = inner && inner.product ? inner.product.id : row && row.product ? row.product.id : null;
				if (pid == null) {
					continue;
				}
				const base = baseline[String(pid)];
				const changed = [];
				for (const key of Object.keys(inner)) {
					if (key === "product" || key === "section") {
						continue;
					}
					const cur = JSON.stringify(inner[key]);
					const old = base ? JSON.stringify(base[key]) : undefined;
					if (cur !== old) {
						changed.push(key);
					}
				}
				if (changed.length) {
					dirty[String(pid)] = changed;
				}
			}
			return dirty;
		},
		// Attach the dirty-field directive to a server-bound payload. localStorage
		// keeps the raw payload; only the server copy carries `_dirty`.
		withDirty(payload) {
			const dirty = this.computeDirtyFields(payload);
			return dirty === null ? payload : { ...payload, _dirty: dirty };
		},
		// Flush the current form state to the SERVER report_drafts row synchronously,
		// awaitably (BEL-1578 Fase E2). After Fase D the manual store() reads
		// report_drafts server-side and ignores posted answers; the async kernel
		// autosync may not have flushed at submit time, so the submit handler must
		// await this BEFORE calling store, or store 422s with empty_draft. Resolves
		// to true on success, false otherwise (caller decides whether to proceed).
		async flushDraftToServer() {
			if (!this.reportDraftKernel || !this.form || !this.form.visit_id || !this.reportType) {
				return false;
			}
			try {
				await this.reportDraftKernel.depositNow(this.form.visit_id, this.reportType, this.withDirty(this.form), {
					deviceMeta: { userAgent: navigator.userAgent, platform: navigator.platform },
					capturedAt: new Date().toISOString(),
				});
				return true;
			} catch (e) {
				return false;
			}
		},
		purgeDraft() {
			localStorage.removeItem(this.draftKeyStorage);
			if (this.reportDraftKernel && this.form.visit_id && this.reportType) {
				this.reportDraftKernel.purge(this.form.visit_id, this.reportType);
			}
		},
		// Arma o atraso de segurança após gravar: o botão "Apagar rascunho" fica
		// desativado durante `seconds` segundos com contagem decrescente (BEL-1580).
		armDeleteDelay(seconds = 3) {
			if (this._deleteCountdownTimer) {
				clearInterval(this._deleteCountdownTimer);
			}
			this.deleteCountdown = seconds;
			this._deleteCountdownTimer = setInterval(() => {
				this.deleteCountdown -= 1;
				if (this.deleteCountdown <= 0) {
					this.deleteCountdown = 0;
					clearInterval(this._deleteCountdownTimer);
					this._deleteCountdownTimer = null;
				}
			}, 1000);
		},
		// Modal de confirmação antes de apagar (BEL-1580). Não faz nada enquanto a
		// contagem decrescente estiver ativa. Usa SweetAlert2 (offline) com fallback
		// para window.confirm.
		async confirmClearProgress() {
			if (this.deleteCountdown > 0) {
				return;
			}
			if (window.Swal && typeof window.Swal.fire === "function") {
				const result = await window.Swal.fire({
					title: "Apagar rascunho?",
					text: "Esta ação não pode ser revertida.",
					type: "warning",
					showCancelButton: true,
					confirmButtonText: "Sim, apagar",
					cancelButtonText: "Cancelar",
					confirmButtonColor: "#d9534f",
					reverseButtons: true,
				});
				if (!result || (result.value !== true && !result.isConfirmed)) {
					return;
				}
			} else if (!window.confirm("Apagar rascunho? Esta ação não pode ser revertida.")) {
				return;
			}
			this.clearProgress();
		},
		async clearProgress() {
			this.purgeDraft();
			if (this.reportDraftKernel && this.form.visit_id && this.reportType) {
				const tombstoneKey = this.form.visit_id + "draft_tombstone_" + this.reportType;
				const ok = await this.reportDraftKernel.deleteServerDraft(this.form.visit_id, this.reportType);
				if (ok) {
					localStorage.removeItem(tombstoneKey);
				} else {
					// DELETE falhou (offline/5xx): marca o rascunho como apagado neste
					// dispositivo para o checkProgressForm não o re-hidratar do servidor.
					localStorage.setItem(tombstoneKey, String(Date.now()));
				}
			}
			window.location.reload();
		},
		saveProgress() {
			const persist = () => {
				const payload = { ...this.form, _savedByUser: true };
				this.persistDraft(payload, {
					deviceMeta: { userAgent: navigator.userAgent, platform: navigator.platform },
					capturedAt: new Date().toISOString(),
				});
				if (typeof window.toastSuccess === "function") {
					window.toastSuccess("Rascunho gravado com sucesso!");
				}
				this.armDeleteDelay();
			};
			// Captura as fotos do #hiddenImagesDiv para o kernel ANTES de persistir, para
			// o payload do rascunho carregar os refs (photoUuid) — online ou offline.
			// Online: o saveDraft seguinte sincroniza-as logo para o staging.
			if (this.captureOfflineDraftPhotos && this.form.visit_id) {
				this.captureOfflineDraftPhotos(this.form.visit_id, this.reportType).finally(persist);
			} else {
				persist();
			}
		},
		checkProgressForm() {
			// Default baseline = the form as built before any draft load. Re-captured
			// after each load branch so the dirty-diff is taken against what the
			// operator actually saw (BEL-1578 §2.1b).
			this.captureDraftBaseline();
			const storedDraft = localStorage.getItem(this.draftKeyStorage);
			if (storedDraft) {
				let parsedDraft = null;
				try {
					parsedDraft = JSON.parse(storedDraft);
				} catch (e) {
					// Rascunho corrompido (escrita truncada por bateria/quota): descarta
					// e segue para a recuperação pelo servidor, em vez de rebentar o
					// mount do formulário com um SyntaxError.
					localStorage.removeItem(this.draftKeyStorage);
				}
				if (parsedDraft) {
					this.form = parsedDraft;
					this.hasProgress = true;
					// Reconstruir a tabela quando o formulário usa um cache filtrado
					// (ex.: Attendance/Linear com filterRowsBide). Competitor usa um
					// computed reativo, logo atualiza sozinho ao trocar this.form.
					if (typeof this.filterRowsMethod === "function") {
						this.filterRowsMethod();
					}
					// Baseline BEFORE seeding: the seed must diff as "nothing dirty" so
					// it preserves any newer Conferência edits instead of clobbering them.
					this.captureDraftBaseline();
					this.seedDraftFromLocal();
					this.$nextTick(() => this.rehydrateDraftPhotos());
					return;
				}
			}
			// Recuperação (BEL-1578 §9): sem rascunho local, tenta o servidor — visita
			// reaberta após o fecho (cliente limpo) ou noutro dispositivo — e traz para
			// o localStorage para recuperar campos + fotos.
			if (this.reportDraftKernel && this.form.visit_id && this.reportType) {
				const tombstoneKey = this.form.visit_id + "draft_tombstone_" + this.reportType;
				if (localStorage.getItem(tombstoneKey)) {
					// O utilizador apagou este rascunho neste dispositivo mas o DELETE
					// ainda não chegou ao servidor — não re-hidratar; retenta o delete
					// em fundo.
					this.reportDraftKernel.deleteServerDraft(this.form.visit_id, this.reportType).then(ok => {
						if (ok) {
							localStorage.removeItem(tombstoneKey);
						}
					});
					return;
				}
				this.reportDraftKernel
					.loadDraft(this.form.visit_id, this.reportType)
					.then(payload => {
						if (!payload) {
							return;
						}
						localStorage.setItem(this.draftKeyStorage, JSON.stringify(payload));
						this.form = payload;
						this.hasProgress = true;
						this.captureDraftBaseline();
						if (typeof this.filterRowsMethod === "function") {
							this.filterRowsMethod();
						}
						this.$nextTick(() => this.rehydrateDraftPhotos());
					})
					.catch(() => {});
			}
		},

		// Captura as fotos do #hiddenImagesDiv (camara + galeria) para o kernel e
		// regista os refs (photoUuid) em this.form.photos, para o payload do rascunho
		// as carregar. Partilhado pelos 3 reportes (linear/attendance/competitor).
		async captureOfflineDraftPhotos(visitId, reportType) {
			if (!this.reportDraftKernel || !visitId) {
				return;
			}

			const inputs = document.querySelectorAll("#hiddenImagesDiv > input");

			for (const input of inputs) {
				if (input.dataset.offlineUuid) {
					continue;
				}

				let dataUrl = null;
				let capturedAt = null;
				try {
					const parsed = JSON.parse(input.value);
					if (Array.isArray(parsed)) {
						dataUrl = parsed[0];
						capturedAt = parsed[1] || null;
					}
				} catch (e) {
					dataUrl = null;
				}

				if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) {
					continue;
				}

				try {
					// Descodifica sem fetch(): offline o browser rejeita fetch() para
					// qualquer esquema (incl. data:), o que descartava as fotos em
					// silêncio. Helper partilhado com a captura do assistente.
					const blob = dataUrlToBlob(dataUrl);
					const photoUuid = await this.reportDraftKernel.capturePhoto(visitId, reportType, blob, {
						capturedAt: capturedAt || new Date().toISOString(),
					});
					input.dataset.offlineUuid = photoUuid;
					this.form.photos = Array.isArray(this.form.photos) ? this.form.photos : [];
					this.form.photos.push({ photoUuid, status: "pending" });
				} catch (e) {
					// Disco cheio: as próximas fotos também falham — avisa e pára, em
					// vez de descartar tudo em silêncio.
					if (isQuotaExceeded(e)) {
						if (typeof window.toastError === "function") {
							window.toastError(
								"Memória do dispositivo cheia — não foi possível guardar as fotos. Sincronize quando houver rede e tente novamente."
							);
						}
						break;
					}
					// best-effort: ignora esta foto e tenta na proxima gravacao
				}
			}
		},
		// Re-hidrata o widget a partir do rascunho: por cada ref (photoUuid) cria um
		// cartao .eachPhotoDiv (com botao de remover) e acerta o contador. A miniatura
		// vem do blob local (IndexedDB, offline/nao-sincronizada) ou, se ja sincronizada,
		// de um GET presigned ao staging. A foto anexa via staging no submit.
		async rehydrateDraftPhotos() {
			const preview = document.getElementById("photosPreview");
			if (!preview || !Array.isArray(this.form.photos)) {
				return;
			}
			const remoteUuids = [];
			for (const photo of this.form.photos) {
				const uuid = photo && photo.photoUuid;
				if (!uuid || preview.querySelector(`.eachPhotoDiv[data-draft-uuid="${uuid}"]`)) {
					continue;
				}
				const card = document.createElement("div");
				card.className = "eachPhotoDiv col-6 col-md-4 no-padding mt-2";
				card.setAttribute("data-draft-uuid", uuid);
				card.style.position = "relative";
				card.innerHTML =
					'<span class="deleteDraftPhoto" data-uuid="' +
					uuid +
					'" style="position:absolute;top:0;right:0;z-index:2;cursor:pointer;background:rgba(0,0,0,.55);color:#fff;padding:2px 9px;border-radius:0 0 0 6px;">&times;</span>' +
					'<img class="previewImage draftThumb" alt="" style="max-width:100%;display:none;" />' +
					'<div class="draftPhotoPlaceholder" style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:90px;background:#eef1f5;border:1px dashed #c3ccd6;border-radius:6px;color:#7a8794;"><i class="mdi mdi-image" style="font-size:1.6rem;"></i><small>Rascunho</small></div>';
				preview.appendChild(card);

				let localSrc = null;
				try {
					const rec = this.reportDraftKernel ? await this.reportDraftKernel.getPhoto(uuid) : null;
					if (rec && rec.blob) {
						localSrc = URL.createObjectURL(rec.blob);
						this._draftObjectUrls[uuid] = localSrc;
					}
				} catch (e) {
					localSrc = null;
				}
				if (localSrc) {
					this._setDraftThumb(card, localSrc);
				} else {
					remoteUuids.push(uuid);
				}
			}
			this.$nextTick(() => {
				this.totalPhotosAmount = document.querySelectorAll(".eachPhotoDiv").length;
			});

			if (remoteUuids.length && navigator.onLine && this.reportDraftKernel && this.form.visit_id) {
				try {
					const previews = await this.reportDraftKernel.photoPreviews(this.form.visit_id, this.reportType, remoteUuids);
					previews.forEach(p => {
						const card = preview.querySelector(`.eachPhotoDiv[data-draft-uuid="${p.photoUuid}"]`);
						if (card && p.url) {
							this._setDraftThumb(card, p.url);
						}
					});
				} catch (e) {
					// sem rede/erro: fica o placeholder
				}
			}
		},
		_setDraftThumb(card, src) {
			const img = card.querySelector(".draftThumb");
			const ph = card.querySelector(".draftPhotoPlaceholder");
			if (img) {
				img.src = src;
				img.style.display = "";
			}
			if (ph) {
				ph.style.display = "none";
			}
		},
		removeDraftPhoto(uuid) {
			if (!uuid) {
				return;
			}
			const card = document.querySelector(`.eachPhotoDiv[data-draft-uuid="${uuid}"]`);
			if (card) {
				card.remove();
			}
			if (this._draftObjectUrls && this._draftObjectUrls[uuid]) {
				URL.revokeObjectURL(this._draftObjectUrls[uuid]);
				delete this._draftObjectUrls[uuid];
			}
			if (Array.isArray(this.form.photos)) {
				this.form.photos = this.form.photos.filter(photo => !photo || photo.photoUuid !== uuid);
			}
			if (this.reportDraftKernel) {
				this.reportDraftKernel.removePhoto(uuid);
			}
			this.persistDraft(
				{ ...this.form, _savedByUser: true },
				{
					deviceMeta: { userAgent: navigator.userAgent, platform: navigator.platform },
					capturedAt: new Date().toISOString(),
				}
			);
			this.$nextTick(() => {
				this.totalPhotosAmount = document.querySelectorAll(".eachPhotoDiv").length;
			});
			if (typeof window.toastSuccess === "function") {
				window.toastSuccess("Foto removida do rascunho.");
			}
		},
	},
};

window.reportDraftMixin = reportDraftMixin;
